import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, CheckCircle2, Circle } from "lucide-react";
import { getDayKey, RoadmapWeek } from "@/lib/roadmapHelpers";
import { cn } from "@/lib/utils";

interface RoadmapCalendarProps {
  roadmap: RoadmapWeek[];
  isDayCompleted: (dayKey: string) => boolean;
  currentDayNumber?: number;
  onDayClick?: (dayNumber: number) => void;
  className?: string;
}

export const RoadmapCalendar = ({
  roadmap,
  isDayCompleted,
  currentDayNumber,
  onDayClick,
  className,
}: RoadmapCalendarProps) => {
  // Create a flat array of all days with their absolute numbers
  const allDays = useMemo(() => {
    const days: Array<{
      absoluteDay: number;
      week: number;
      day: number;
      title: string;
      isCompleted: boolean;
    }> = [];

    roadmap.forEach((week) => {
      week.days.forEach((day) => {
        const absoluteDay = ((week.week - 1) * 7) + day.day;
        const dayKey = getDayKey(week.week, day.day);
        days.push({
          absoluteDay,
          week: week.week,
          day: day.day,
          title: day.title,
          isCompleted: isDayCompleted(dayKey),
        });
      });
    });

    // Sort by absolute day and limit to first 28 days
    const sortedDays = days.sort((a, b) => a.absoluteDay - b.absoluteDay);
    const limitedDays = sortedDays.slice(0, 28);
    
    // Renumber days to be sequential 1-28 for calendar display
    return limitedDays.map((day, index) => ({
      ...day,
      absoluteDay: index + 1, // Sequential numbering 1-28
    }));
  }, [roadmap, isDayCompleted]);

  // Group days into weeks (every 7 days)
  const weeks = useMemo(() => {
    const result: Array<{
      weekNumber: number;
      days: typeof allDays;
    }> = [];

    for (let i = 0; i < allDays.length; i += 7) {
      result.push({
        weekNumber: Math.floor(i / 7) + 1,
        days: allDays.slice(i, i + 7),
      });
    }

    return result;
  }, [allDays]);

  const completedCount = allDays.filter((d) => d.isCompleted).length;
  const progressPercentage = Math.round((completedCount / allDays.length) * 100);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            <CardTitle className="text-lg">28-Day Calendar View</CardTitle>
          </div>
          <Badge variant="outline">
            {completedCount}/{allDays.length} days
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Visual overview of your learning journey
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {weeks.map((week) => (
          <div key={week.weekNumber}>
            <h3 className="text-sm font-semibold mb-3">
              Week {week.weekNumber}
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {week.days.map((dayData) => (
                <button
                  key={dayData.absoluteDay}
                  onClick={() => onDayClick?.(dayData.absoluteDay)}
                  className={cn(
                    "relative aspect-square rounded-lg border p-2 transition-all",
                    "hover:shadow-md hover:scale-105",
                    "flex flex-col items-center justify-center text-center",
                    dayData.isCompleted
                      ? "bg-green-500/10 border-green-500/50 hover:bg-green-500/20"
                      : "bg-card border-border hover:bg-accent",
                    currentDayNumber === dayData.absoluteDay &&
                      "ring-2 ring-primary ring-offset-2"
                  )}
                  title={dayData.title}
                >
                  <span className="text-xs font-semibold mb-1">
                    {dayData.absoluteDay}
                  </span>
                  {dayData.isCompleted ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  ) : (
                    <Circle className="h-3 w-3 text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border border-green-500/50 bg-green-500/10" />
            <span className="text-xs text-muted-foreground">Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border border-border bg-card" />
            <span className="text-xs text-muted-foreground">Not Started</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded border-2 border-primary bg-card" />
            <span className="text-xs text-muted-foreground">Current</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
